import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
    WarehouseDeliveryFragmentFragment,
    WarehouseDeliveryInput,
    useUpdateWarehouseDeliveryMutation,
    useWarehouseDeliveryUpdatedSubscription,
} from 'data/__generated__';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PAGES } from 'constants/pages';
import { GeneralInfoSection } from './GeneralInfoSection';
import { StatesSection } from './StatesSection';
import { ProductsSection } from './ProductsSection';
import { warehouseDeliveryUpdatedHandler } from '../utils/warehouseDeliveryUpdatedHandler';
import { Loader, LoaderModeType } from 'components/Loader';
import { toast } from 'react-toastify';
import { DriverSection } from './DriverSection';
import { PhotosSectionSection } from './PhotosSectionSection';

export const WarehouseDeliveryFormContent = ({
    warehouseDelivery,
}: {
    warehouseDelivery: WarehouseDeliveryFragmentFragment;
}) => {
    const [updateWarehouseDelivery, { loading: updateWarehouseDeliveryLoading }] = useUpdateWarehouseDeliveryMutation();
    const methods = useForm<WarehouseDeliveryInput>({
        defaultValues: {
            deliveryDate: warehouseDelivery.deliveryDate,
        },
    });

    // if we want to allow updates later on
    const {
        handleSubmit,
        formState: { isDirty },
        reset,
    } = methods;

    useEffect(() => {
        reset({
            deliveryDate: warehouseDelivery.deliveryDate,
        });
    }, [warehouseDelivery, reset]);

    const onSubmit = handleSubmit(async (fields) => {
        const { data } = await updateWarehouseDelivery({
            variables: {
                warehouseDeliveryId: warehouseDelivery._id,
                warehouseDeliveryInput: fields,
            },
        });
        if (data?.updateWarehouseDelivery.warehouseDelivery) {
            warehouseDeliveryUpdatedHandler(data.updateWarehouseDelivery.warehouseDelivery);

            toast.success('La livraison a été modifiée !');
        }
    });

    useWarehouseDeliveryUpdatedSubscription({
        onData({ data: { data } }) {
            if (!data) return;
            const { warehouseDeliveryUpdated } = data;
            warehouseDeliveryUpdatedHandler(warehouseDeliveryUpdated);
        },
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.warehouseDeliveries.url }],
        },
    ];

    return (
        <FormProvider {...methods}>
            {/* <Form onSubmit={onSubmit}> State section interfere with the form */}
            <Form>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.warehouseDeliveryDetails} />
                    </HeaderTitle>
                    <ActionsAndSave
                        actionGroups={actionGroups}
                        onSave={onSubmit}
                        saveDisabled={!isDirty}
                        saveLoading={updateWarehouseDeliveryLoading}
                        saveTitle="Mettre à jour"
                        saveDataTest="submit-button"
                    />
                </Header>
                <Content>
                    {updateWarehouseDeliveryLoading ? (
                        <Loader size="18px" mode={LoaderModeType.Spin} />
                    ) : (
                        <ScrollableContent>
                            <GeneralInfoSection warehouseDelivery={warehouseDelivery} />
                            <DriverSection warehouseDelivery={warehouseDelivery} />
                            <PhotosSectionSection warehouseDelivery={warehouseDelivery} />
                            <StatesSection warehouseDelivery={warehouseDelivery} />
                            <ProductsSection warehouseDelivery={warehouseDelivery} />
                        </ScrollableContent>
                    )}
                </Content>
            </Form>
        </FormProvider>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
